import {
  IconButton,
  type IconButtonProps,
  Menu as MuiMenu,
  type MenuItemProps,
  type MenuProps,
} from '@mui/material';

import _ from 'lodash';
import React, {
  type JSX,
  cloneElement,
  Children,
  useCallback,
  type PropsWithChildren,
  type ReactElement,
  type MouseEventHandler,
  type MouseEvent,
} from 'react';

import { makeStyles, type DefaultTheme } from '@mui/styles';
import TableMenuIcon from '../../_assets/svg/TableMenuIcon.svg';
import { CloseMenuContext } from './closeMenuContext';

/**
 * What an icon-only menu trigger announces. Shared so the few surfaces that open
 * a SimpleMenu from their own icon read the same as the common one.
 */
export const MENU_TRIGGER_LABEL = 'More options';

/**
 * The icon-only trigger nearly every SimpleMenu opens from. TableMenuIcon is an
 * SVG with no <title>, so aria-label is the button's only accessible name — and
 * now that the wrapper no longer hides it, an unlabelled trigger announces as
 * bare "button" on some sixty surfaces.
 *
 * Props spread last on purpose: SimpleMenu injects its own onClick through
 * cloneElement, and a surface with something more specific to say (a claim, an
 * invoice) passes its own aria-label over the generic one.
 */
export const MenuTriggerButton = (props: Readonly<IconButtonProps>) => (
  <IconButton
    size="small"
    aria-haspopup="true"
    aria-label={MENU_TRIGGER_LABEL}
    {...props}
  >
    <TableMenuIcon />
  </IconButton>
);

interface SimpleMenu extends MenuProps {
  anchor?: JSX.Element;
  minWidth?: string;
  onOpenChange?: (open: boolean) => void;
}

export const SimpleMenu = ({
  anchor = <MenuTriggerButton />,
  children,
  minWidth,
  onOpenChange,
  ...props
}: Omit<SimpleMenu, 'open'>) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const classes = useClasses({ minWidth });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onOpenChange?.(true);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const createClickHandler = useCallback(
    (childOnClick: MouseEventHandler<HTMLLIElement> | undefined) => {
      return (event: MouseEvent<HTMLLIElement>) => {
        if (_.isFunction(childOnClick)) {
          childOnClick(event);
          handleClose();
        }
      };
    },
    [handleClose]
  );

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    // The provider sits outside MuiMenu on purpose. Context crosses the portal
    // MuiMenu renders through, while a wrapper element inside it would leave
    // MenuList with one non-item child and break its focus handling.
    <CloseMenuContext.Provider value={handleClose}>
      {/*
       * This wrapper exists only for stopPropagation, so a menu inside a table row
       * does not also fire the row's click. It cannot move onto the anchor: MuiMenu
       * portals its list, but React bubbles events through the component tree, so
       * item clicks reach the row through here too.
       *
       * role="presentation" and not aria-hidden: aria-hidden would take the whole
       * subtree out of the accessibility tree, leaving the trigger unreachable by a
       * screen reader and invisible to getByRole.
       */}
      <div onClick={(e) => e.stopPropagation()} role="presentation">
        {cloneElement(anchor, {
          'aria-describedby': id,
          onClick: handleClick,
        })}
        <MuiMenu
          id={id}
          open={open}
          autoFocus={false}
          anchorEl={anchorEl}
          onClose={handleClose}
          classes={{ paper: classes.paper }}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          {...props}
        >
          {Children.map(children, (c) => {
            const child = c as ReactElement<PropsWithChildren<MenuItemProps>>;

            if (child?.props) {
              return cloneElement(child, {
                classes: { root: classes.menuItem, ...child.props.classes },
                onClick: createClickHandler(child.props.onClick),
              });
            }
            return <span />;
          })}
        </MuiMenu>
      </div>
    </CloseMenuContext.Provider>
  );
};

const useClasses = makeStyles<DefaultTheme, { minWidth?: string }>(
  ({ config, palette }) => ({
    paper: {
      minWidth: ({ minWidth }) => minWidth ?? 'auto',
      background: palette.common.darkBlue,
      border: `1px solid ${palette.common.almostWhite}`,
      color: palette.common.almostWhite,
    },

    menuItem: {
      padding: '0.4166666667rem 0.8333333333rem',
      color: config.colors.white,

      '&:hover, &:focus': {
        color: config.colors.white,
        fontWeight: 600,
        background: '#113E68',
      },
    },
  })
);
